/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Grid, IconButton, Spacer, Table, ToolTip, useAsyncEffect } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { H4, P } from "@cd2/text"
import { useState } from "react"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { CompactTable } from "../../Pages/Customers/CustomerMonthlySpendGraph"
import { icons } from "../../constants/icons"
import { IRecord } from "../../types/schema"
import { sum } from "../../utils/arrays"
import { ICustomerHeadingFieldsProps, filterDeleted } from "./quoteOrderUtils"
import { brandColors } from "../../Components/EOSThemeProvider"

export const incoTermOptions = [
  { label: "DAP-CLEARED", value: "DAP-CLEARED" },
  { label: "EX-WORKS", value: "EX-WORKS" },
] as const

export function CommercialInvoiceInput({
  quote,
  onChange,
  errors: _,
}: ICustomerHeadingFieldsProps) {
  const [isCommercialInvoiceActive, setIsCommercialInvoiceActive] = useState(false)

  return (
    <FloatingBox
      css={css`
        ${FloatingBox.vars.headerPadding}: 0;
      `}
      header={
        <div
          css={css`
            cursor: pointer;
          `}
          onClick={() => setIsCommercialInvoiceActive(!isCommercialInvoiceActive)}
        >
          <Grid columns={[{ fr: 1 }, "max-content"]} alignItems="center">
            <div
              css={css`
                padding: 16px 12px;
              `}
            >
              <P blockSpacing={0}>Commercial invoice details</P>
            </div>
            <div>
              <IconButton
                icon={isCommercialInvoiceActive ? icons.chevronUp : icons.chevronDown}
                buttonStyle="text"
                square
              />
            </div>
          </Grid>
        </div>
      }
    >
      {isCommercialInvoiceActive && (
        <Grid gap={16}>
          <Fields.Text
            label="Order origin"
            placeholder="Origin: China"
            onChange={origin => onChange({ origin })}
            value={quote.origin ?? ""}
          />
          <Fields.Text
            label="Order Weight (Kg)"
            postFieldText={
              <ToolTip
                hoverContent={() => <ProductWeightTable items={filterDeleted(quote.items)} />}
                openOn="click"
              >
                <div
                  css={css`
                    cursor: pointer;
                  `}
                >
                  <P color={brandColors.mint}>View Item Weights</P>
                </div>
              </ToolTip>
            }
            placeholder="Gross Weight: 150 KG"
            onChange={weight => onChange({ weight })}
            value={quote.weight ?? ""}
          />
          <Fields.Text
            label="Order Dimensions"
            placeholder="120 x 80 x 100 cm"
            onChange={dimensions => onChange({ dimensions })}
            value={quote.dimensions ?? ""}
          />
          <Fields.Text
            label="Order # of parcels"
            placeholder="1 parcel"
            onChange={pallet_size => onChange({ pallet_size })}
            value={quote.pallet_size ?? ""}
          />
          <Fields.Select2
            label="INCO Terms"
            options={incoTermOptions}
            onChange={opt => onChange({ incoTerms: opt.value })}
            value={incoTermOptions.find(opt => opt.value === quote.incoTerms)}
          />
          <Fields.TextArea
            label="Shipper Address"
            onChange={value => onChange({ shipperAddress: value })}
            value={quote.shipperAddress ?? ""}
            rows="auto"
          />
        </Grid>
      )}
    </FloatingBox>
  )
}

function ProductWeightTable({
  items,
}: {
  items: { quantity: number | null; product?: { id: string } }[]
}) {
  const skeem = window.skeem
  const [productData, setProductData] = useState<
    { stock_code: string; weight: number; quantity: number; total_weight: number }[]
  >([])

  async function loadWeights() {
    const productIds = items.map(x => x.product?.id).filter(Boolean)
    const products = await skeem.fetch2<
      (IRecord<"products", "stock_code"> & {
        product_master?: IRecord<"product_masters", "weight">
      })[]
    >({
      model: {
        name: "products",
        attributes: ["stock_code", { name: "product_master", attributes: ["weight"] }],
        matches: { id: { or: productIds } },
      },
    })
    const data: typeof productData = []
    for (const item of items) {
      const product = products.find(x => x.id === item.product?.id)
      if (!product) {
        continue
      }
      data.push({
        stock_code: product.stock_code,
        quantity: item.quantity ?? 1,
        weight: product.product_master?.weight ?? 0,
        total_weight: (product.product_master?.weight ?? 0) * (item.quantity ?? 1),
      })
    }
    setProductData(data)
  }

  useAsyncEffect(loadWeights, [JSON.stringify(items.map(x => [x.product?.id, x.quantity]))])

  return (
    <div>
      <H4>Item Weights</H4>
      <Spacer />
      <CompactTable>
        <Table data={productData ?? []}>
          <Table.DataColumn heading="Product" field="stock_code" />
          <Table.DataColumn heading="Weight (KG)" field="weight" />
          <Table.DataColumn heading="Qty" field="quantity" />
          <Table.DataColumn heading="Total Weight (KG)" field="total_weight" />
        </Table>
      </CompactTable>
      <Spacer />
      <P>Est. Total Weight: {sum(productData, x => x.total_weight)} KG</P>
    </div>
  )
}
